import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/admin/AdminAuth";
import AdminArc from "@/admin/AdminArc";

function StatCard({ label, value, accent }) {
  return (
    <div className="border border-dh-cream/10 bg-dh-bg2 p-6">
      <p className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">{label}</p>
      <p className={`mt-3 font-display text-4xl ${accent || "text-dh-cream"}`}>{value}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: "border-emerald-500/40 text-emerald-300",
    failed: "border-red-500/40 text-red-300",
    pending: "border-dh-cream/20 text-dh-muted",
  };
  const cls = map[status] || map.pending;
  return (
    <span className={`inline-block border px-2.5 py-1 font-body text-[10px] uppercase tracking-[0.15em] ${cls}`}>
      {status || "pending"}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const { admin, logout, authHeader } = useAdminAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [tab, setTab] = useState("newsletter");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [bSubject, setBSubject] = useState("");
  const [bBody, setBBody] = useState("");
  const [bAudience, setBAudience] = useState(null);
  const [bConfirm, setBConfirm] = useState(false);
  const [bSending, setBSending] = useState(false);
  const [bResult, setBResult] = useState(null);

  const openBroadcast = async () => {
    setShowBroadcast(true);
    setBConfirm(false);
    setBResult(null);
    try {
      const res = await api.get("/admin/newsletter/broadcast/audience", { headers: authHeader() });
      setBAudience(res.data.active_subscribers);
    } catch { setBAudience(null); }
  };

  const sendBroadcast = async () => {
    setBSending(true);
    try {
      const res = await api.post("/admin/newsletter/broadcast",
        { subject: bSubject, body: bBody }, { headers: authHeader(), timeout: 120000 });
      setBResult(res.data);
      setBConfirm(false);
      toast.success(`Broadcast sent to ${res.data.sent} subscriber${res.data.sent === 1 ? "" : "s"}.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Broadcast failed.");
    } finally { setBSending(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/newsletter-signups", { headers: authHeader() });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Could not load signups.");
      }
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/newsletter-signups.csv", {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dhamma-newsletter-signups-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error("Export failed.");
      }
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-dh-bg px-5 py-12 sm:px-10">
      <div className="mx-auto max-w-[1200px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-body text-[11px] uppercase tracking-ultra text-dh-gold">Dhamma · Private Admin</p>
            <h1 className="mt-3 font-display text-4xl text-dh-cream sm:text-5xl">Admin Dashboard</h1>
            <p className="mt-2 font-body text-sm text-dh-muted">Signed in as {admin?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {tab === "newsletter" && (
              <>
                <button
                  onClick={openBroadcast}
                  data-testid="admin-new-broadcast"
                  className="inline-flex items-center gap-2 border border-dh-gold/60 px-4 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2"
                >
                  New Broadcast
                </button>
                <button
                  onClick={exportCsv}
                  data-testid="admin-export-csv"
                  disabled={exporting || loading}
                  className="inline-flex items-center gap-2 border border-dh-gold/60 px-4 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-50"
                >
                  <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
                </button>
                <button
                  onClick={load}
                  data-testid="admin-refresh"
                  className="inline-flex items-center gap-2 border border-dh-cream/20 px-4 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] text-dh-cream transition-colors hover:border-dh-gold hover:text-dh-gold"
                >
                  <RefreshCw size={14} /> Refresh
                </button>
              </>
            )}
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-dh-cream/20 px-4 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] text-dh-cream transition-colors hover:border-red-400/60 hover:text-red-300"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2 border-b border-dh-cream/10">
          {[["newsletter", "Newsletter"], ["arc", "ARC Applications"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-5 py-3 font-body text-[11px] uppercase tracking-[0.18em] transition-colors ${tab === key ? "border-b-2 border-dh-gold text-dh-gold" : "text-dh-muted hover:text-dh-cream"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "arc" ? (
          <div className="mt-8"><AdminArc /></div>
        ) : (
        <>
        {showBroadcast && (
          <div className="mt-10 border border-dh-gold/25 bg-dh-bg2/50 p-6 sm:p-8" data-testid="broadcast-panel">
            <div className="flex items-center justify-between">
              <p className="font-body text-[10px] uppercase tracking-ultra text-dh-muted">New Broadcast</p>
              <button data-testid="broadcast-close" onClick={() => { setShowBroadcast(false); setBConfirm(false); setBResult(null); }} className="font-body text-xs text-dh-muted hover:text-dh-cream">Close</button>
            </div>
            <p className="mt-2 font-body text-xs text-dh-muted">
              Goes to {bAudience === null ? "…" : bAudience} active subscriber{bAudience === 1 ? "" : "s"} (unsubscribed readers are never included). Each email carries its own unsubscribe link.
            </p>
            <input
              data-testid="broadcast-subject"
              value={bSubject}
              onChange={(e) => setBSubject(e.target.value)}
              placeholder="Subject line"
              maxLength={200}
              className="mt-4 w-full border border-dh-cream/15 bg-dh-bg px-3 py-2.5 font-body text-sm text-dh-cream outline-none focus:border-dh-gold"
            />
            <textarea
              data-testid="broadcast-body"
              value={bBody}
              onChange={(e) => setBBody(e.target.value)}
              placeholder="Write your announcement… (blank lines create paragraphs)"
              rows={7}
              className="mt-3 w-full resize-y border border-dh-cream/15 bg-dh-bg px-3 py-2.5 font-body text-sm text-dh-cream outline-none focus:border-dh-gold"
            />
            {!bConfirm && !bResult && (
              <button
                data-testid="broadcast-continue"
                disabled={!bSubject.trim() || !bBody.trim()}
                onClick={() => setBConfirm(true)}
                className="mt-4 border border-dh-gold/70 px-6 py-2.5 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40"
              >
                Review & Send
              </button>
            )}
            {bConfirm && (
              <div data-testid="broadcast-confirm-dialog" className="mt-4 border border-dh-gold/40 bg-dh-bg2/70 p-5">
                <p className="font-body text-sm text-dh-cream">
                  Send "{bSubject.trim()}" to {bAudience ?? "your"} active subscriber{bAudience === 1 ? "" : "s"} now?
                </p>
                <div className="mt-4 flex items-center gap-4">
                  <button
                    data-testid="broadcast-send"
                    disabled={bSending || bAudience === 0}
                    onClick={sendBroadcast}
                    className="border border-dh-gold/70 px-5 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-dh-gold transition-colors hover:bg-dh-gold hover:text-dh-bg2 disabled:opacity-40"
                  >
                    {bSending ? "Sending…" : `Send to ${bAudience ?? ""} Subscriber${bAudience === 1 ? "" : "s"}`}
                  </button>
                  <button data-testid="broadcast-cancel" onClick={() => setBConfirm(false)} className="font-body text-[10px] uppercase tracking-[0.16em] text-dh-muted underline-offset-4 hover:text-dh-cream hover:underline">
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {bResult && (
              <p data-testid="broadcast-result" className="mt-4 font-body text-sm text-dh-cream">
                Done — {bResult.sent} sent{bResult.failed ? `, ${bResult.failed} failed` : ""} of {bResult.total_recipients} subscribers.
              </p>
            )}
          </div>
        )}
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Total Signups" value={data?.stats?.total ?? "—"} />
          <StatCard label="Welcome Sent" value={data?.stats?.sent ?? "—"} accent="text-emerald-300" />
          <StatCard label="Delivery Failed" value={data?.stats?.failed ?? "—"} accent="text-red-300" />
          <StatCard label="Unsubscribed" value={data?.stats?.unsubscribed ?? "—"} accent="text-dh-gold" />
        </div>

        <div className="mt-10 overflow-x-auto border border-dh-cream/10">
          <table className="w-full min-w-[760px] border-collapse" data-testid="signups-table">
            <thead>
              <tr className="border-b border-dh-cream/10 bg-dh-bg2 text-left">
                <th className="p-4 font-body text-[10px] uppercase tracking-[0.15em] text-dh-muted">Email</th>
                <th className="p-4 font-body text-[10px] uppercase tracking-[0.15em] text-dh-muted">Signed Up</th>
                <th className="p-4 font-body text-[10px] uppercase tracking-[0.15em] text-dh-muted">Welcome Status</th>
                <th className="p-4 font-body text-[10px] uppercase tracking-[0.15em] text-dh-muted">Delivery Error</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center font-body text-sm text-dh-muted">Loading…</td>
                </tr>
              ) : data?.signups?.length ? (
                data.signups.map((s, i) => (
                  <tr key={i} className="border-b border-dh-cream/5 align-top" data-testid="signup-row">
                    <td className="p-4">
                      <p className="font-body text-sm text-dh-cream">{s.email}</p>
                      {s.unsubscribed && (
                        <span className="mt-1 inline-block font-body text-[10px] uppercase tracking-[0.15em] text-dh-gold">Unsubscribed</span>
                      )}
                    </td>
                    <td className="p-4 font-body text-sm text-dh-muted">{fmtDate(s.created_at)}</td>
                    <td className="p-4"><StatusPill status={s.welcome_email_status} /></td>
                    <td className="p-4 max-w-[360px]">
                      <p className="font-body text-xs leading-relaxed text-dh-muted break-words">{s.welcome_error || "—"}</p>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="p-8 text-center font-body text-sm text-dh-muted">No signups yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
